export const queryKeys = {
  teamRosterPlayers: {
    all: ['teamRosterPlayers'],
    byTeam: (teamId) => [...queryKeys.teamRosterPlayers.all, teamId],
    byRoster: (teamId, rosterId) => [
      ...queryKeys.teamRosterPlayers.byTeam(teamId),
      rosterId,
    ],
    // Single roster player (GET .../players/:playerId), used to seed the Edit
    // Player form. Deliberately nested UNDER byRoster so React Query's prefix
    // matching makes every existing list invalidation refresh the detail too.
    byPlayer: (teamId, rosterId, playerId) => [
      ...queryKeys.teamRosterPlayers.byRoster(teamId, rosterId),
      playerId,
    ],
  },
  game: {
    all: ['game'],
    byId: (gameId, isPublic) => [...queryKeys.game.all, gameId, !!isPublic],
  },
  // The Schedule > Games grid (List / Calendar / Details). Deliberately its OWN
  // root, never nested under `game.all`: React Query matches by key PREFIX, so
  // any existing `invalidateQueries(game.all)` — the game page, the scoring
  // screens — would refetch the whole paginated grid and throw away the admin's
  // scroll position and selection mid-edit.
  scheduleGames: {
    all: ['scheduleGames'],
    // `fingerprint` is a plain object; react-query stable-hashes it, so two
    // filter combinations can never collide and a stale response can never be
    // rendered under a newer key.
    byFingerprint: (fingerprint) => [
      ...queryKeys.scheduleGames.all,
      fingerprint ?? null,
    ],
  },
  // The day index behind the calendar's opening month (GET /games/dates). Its
  // own root rather than a `scheduleGames` child: invalidating the grid after a
  // bulk retime must not also refetch this, and a stale day index is harmless —
  // it decides which month to OPEN on, not what that month contains.
  scheduleGameDates: {
    all: ['scheduleGameDates'],
    byFingerprint: (fingerprint) => [
      ...queryKeys.scheduleGameDates.all,
      fingerprint ?? null,
    ],
  },
  // Location/facility options behind the add/edit Game modal's pickers.
  // `scheduleId` and `recursive` both change what the endpoint returns, so both
  // are part of the key -- the "All Locations" / "Within organization" toggle
  // flips `recursive` and must not read the other list's cache.
  venues: {
    all: ['venues'],
    bySchedule: (scheduleId, recursive) => [
      ...queryKeys.venues.all,
      scheduleId ?? null,
      recursive ?? true,
    ],
  },
  venueFacilities: {
    all: ['venueFacilities'],
    byVenue: (venueId) => [...queryKeys.venueFacilities.all, venueId],
  },
  // The competition's child levels (GET /schedules/:id/children/dropdown).
  // Only fetched when a placeholder team is selected, which is the one case
  // the admin has to name a division by hand.
  subCompetitions: {
    all: ['subCompetitions'],
    bySchedule: (scheduleId) => [
      ...queryKeys.subCompetitions.all,
      scheduleId ?? null,
    ],
  },
  // Team options for the home/visiting pickers (GET /schedules/:id/teams/simple).
  scheduleTeams: {
    all: ['scheduleTeams'],
    bySchedule: (scheduleId) => [...queryKeys.scheduleTeams.all, scheduleId],
  },
  // The officials an admin can assign to a game, scoped to one organization +
  // competition. Its own root: this is a slow-moving directory of people, not
  // part of any game's state, so a bulk retime must not throw it away.
  adminOfficialSearch: {
    all: ['adminOfficialSearch'],
    byScope: (organizationId, scheduleId) => [
      ...queryKeys.adminOfficialSearch.all,
      organizationId ?? null,
      scheduleId ?? null,
    ],
  },
  gameRosters: {
    all: ['gameRosters'],
    byGame: (gameId) => [...queryKeys.gameRosters.all, gameId],
  },
  gameRosterPlayers: {
    all: ['gameRosterPlayers'],
    byRoster: (gameId, gameRosterId) => [
      ...queryKeys.gameRosterPlayers.all,
      gameId,
      gameRosterId,
    ],
  },
  gameRosterUsers: {
    all: ['gameRosterUsers'],
    byRoster: (gameId, gameRosterId) => [
      ...queryKeys.gameRosterUsers.all,
      gameId,
      gameRosterId,
    ],
  },
  player: {
    all: ['player'],
    byId: (playerId) => [...queryKeys.player.all, playerId],
  },
  // Self-scoped player profile for the Edit Player flow. Distinct from
  // `player.byId` because it hits GET /users/me/players/:id (a different
  // endpoint/permission scope than the public GET /players/:id used by the
  // Player page) — sharing a key would collide and cross-invalidate.
  playerProfile: {
    all: ['playerProfile'],
    byId: (playerId) => [...queryKeys.playerProfile.all, playerId],
  },
  playerHighlights: {
    all: ['playerHighlights'],
    byPlayer: (playerId) => [...queryKeys.playerHighlights.all, playerId],
    byProfilePlayers: (playerIds) => [
      ...queryKeys.playerHighlights.all,
      'profile',
      playerIds,
    ],
    // The horizontal "Career Highlights" list (ProfileHighlightsList) filters
    // and sorts differently than the grid tab, so it gets its own key to avoid
    // colliding with byProfilePlayers in the cache.
    byProfileList: (playerIds) => [
      ...queryKeys.playerHighlights.all,
      'profile-list',
      playerIds,
    ],
  },
  playerSeasonStats: {
    all: ['playerSeasonStats'],
    byPlayerSport: (playerId, sportId, genericSportId, goalie) => [
      ...queryKeys.playerSeasonStats.all,
      playerId ?? null,
      sportId ?? null,
      genericSportId ?? null,
      !!goalie,
    ],
  },
  userPlayers: {
    all: ['userPlayers'],
    me: () => [...queryKeys.userPlayers.all, 'me'],
  },
  userFamily: {
    all: ['userFamily'],
    me: () => [...queryKeys.userFamily.all, 'me'],
  },
  playerFamily: {
    all: ['playerFamily'],
    byPlayer: (playerId) => [...queryKeys.playerFamily.all, playerId],
  },
  tutorial: {
    all: ['tutorial'],
    byKey: (key) => [...queryKeys.tutorial.all, key],
  },
  selfUser: {
    all: ['selfUser'],
    me: () => [...queryKeys.selfUser.all, 'me'],
  },
  highlight: {
    all: ['highlight'],
    byId: (highlightId) => [...queryKeys.highlight.all, highlightId],
  },
  myRegistrations: {
    all: ['myRegistrations'],
  },
  userSuspensions: {
    all: ['userSuspensions'],
    me: (onlyActive) => [
      ...queryKeys.userSuspensions.all,
      'me',
      onlyActive ? 1 : 0,
    ],
  },
  pendingInstallments: {
    all: ['pendingInstallments'],
  },
  pendingDeposits: {
    all: ['pendingDeposits'],
  },
  userBilling: {
    all: ['userBilling'],
    byId: (billingId) => [...queryKeys.userBilling.all, billingId],
  },
  gameTimeline: {
    all: ['gameTimeline'],
    byGame: (gameId) => [...queryKeys.gameTimeline.all, gameId],
  },
  gameEvents: {
    all: ['gameEvents'],
    byGame: (gameId) => [...queryKeys.gameEvents.all, gameId],
  },
  events: {
    all: ['events'],
    byId: (eventId) => [...queryKeys.events.all, eventId],
    // Nested under `all` so invalidating the namespace also drops the lists.
    list: (...args) => [...queryKeys.events.all, 'list', ...args],
    byRoster: (teamId, rosterId) =>
      queryKeys.events.list(teamId ?? null, rosterId ?? null),
    // Distinct tail from byRoster so "All Seasons" never collides with a
    // roster-scoped entry for the same team.
    byTeam: (teamId) => queryKeys.events.list(teamId ?? null, 'all-seasons'),
    // My Schedule's cross-team list. The whole filter object is part of the
    // identity — every filter is sent to the endpoint, so two different filter
    // sets are two different responses and must not share a cache entry.
    byMembership: (query) => queryKeys.events.list('membership', query ?? null),
    // A competition/season/division's full list, which answers for everyone
    // rather than the viewer's own memberships. Distinct tail from
    // byMembership so the two can never share an entry: the same filters sent
    // to the two endpoints give different answers.
    bySchedule: (scheduleId, query) =>
      queryKeys.events.list('schedule', scheduleId ?? null, query ?? null),
  },
  gameScoreboard: {
    all: ['gameScoreboard'],
    byGame: (gameId) => [...queryKeys.gameScoreboard.all, gameId],
  },
  gameSettings: {
    all: ['gameSettings'],
    byGame: (gameId) => [...queryKeys.gameSettings.all, gameId],
  },
  orgCompetitions: {
    all: ['orgCompetitions'],
    byOrg: (orgId) => [...queryKeys.orgCompetitions.all, orgId],
    bySearch: (orgId, search, filters) => [
      ...queryKeys.orgCompetitions.byOrg(orgId),
      search,
      // filters object is stable-stringified by react-query's key hashing,
      // so different sport filter combos get distinct cache entries.
      filters ?? null,
    ],
  },
  // Admin player search (roster "Add Player" search box). Keyed by org + query +
  // global flag so each settled (debounced) query caches independently.
  adminPlayerSearch: {
    all: ['adminPlayerSearch'],
    byQuery: (organizationId, query, isGlobal) => [
      ...queryKeys.adminPlayerSearch.all,
      organizationId ?? null,
      query,
      !!isGlobal,
    ],
  },
  teamSearch: {
    all: ['teamSearch'],
    notRegistered: (scheduleId, query, isGlobal) => [
      ...queryKeys.teamSearch.all,
      'notRegistered',
      scheduleId,
      query,
      isGlobal,
    ],
  },
  // Scoring-context settings: same endpoint as gameSettings, but stored
  // verbatim from req() (no unwrapping) to match what ScoringContext's
  // setSettings(data) has stored historically. Keep this separate from
  // gameSettings so we don't fight over the cache shape with the existing
  // common/features/game/hooks/useGameSettings hook (which unwraps).
  scoringContextSettings: {
    all: ['scoringContextSettings'],
    byGame: (gameId) => [...queryKeys.scoringContextSettings.all, gameId],
  },
  gameHighlights: {
    all: ['gameHighlights'],
    byGame: (gameId) => [...queryKeys.gameHighlights.all, gameId],
  },
  gameShootout: {
    all: ['gameShootout'],
    byGame: (gameId) => [...queryKeys.gameShootout.all, gameId],
  },
  // Relationship vocabulary (GET /types/relationship). Static reference data —
  // cached 1h and shared by the family invite/edit + roster link-to-existing
  // pickers across web + mobile.
  relationshipTypes: {
    all: ['relationshipTypes'],
    // Same endpoint but keeps the "Me"/Self entry (edit-family flow only). Kept
    // under a distinct key so it never collides with the filtered `all` cache.
    allWithSelf: ['relationshipTypes', 'withSelf'],
  },
  // Provinces/states for a country (GET /countries/:id/provinces). Geo data
  // never changes, so this is cached aggressively (1h staleTime) and shared
  // across mobile + web edit-player and roster-player flows.
  provinces: {
    all: ['provinces'],
    byCountry: (countryId) => [...queryKeys.provinces.all, countryId ?? null],
  },
  // Admin-scoped registration payload (GET /registration/:scheduleId/admin).
  // Distinct from the legacy non-RQ registration fetch in useRegistrationData —
  // this is the consolidated admin view intended to replace the modal's
  // separate registration/account/waivers/analytics calls.
  registrationAdmin: {
    all: ['registrationAdmin'],
    bySchedule: (scheduleId) => [
      ...queryKeys.registrationAdmin.all,
      scheduleId ?? null,
    ],
  },
}
